import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Table } from '@consta/uikit/Table'
import { Text } from '@consta/uikit/Text'
import { FC, useState, useEffect } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { IconCheck } from '@consta/icons/IconCheck'
import { 
    useLazyGetLaboratoryPlanQuery,
    usePostLaboratoryPlanMutation 
} from '../../../services/miniportService';
import { useLazyGetProjectUuidQuery } from '../../../services/projectService'

type MonitoringStage5Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    handleCloseModal: () => void;
    projectUuid: string;
}

type LaboratoryPlanRow = {
    id: string;
    well_number: string;
    water_ic?: boolean | any;
    water_ha?: boolean | any;
    water_kapel?: boolean | any;
    oil_gh?: boolean | any;
    category?: string | any;
};

const MonitoringStage5: FC<MonitoringStage5Props> = ({ 
    isOpen, 
    setIsOpen, 
    handleCloseModal,
    projectUuid 
}) => {
    const [waterData, setWaterData] = useState<LaboratoryPlanRow[]>([]);
    const [oilData, setOilData] = useState<LaboratoryPlanRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [getProject, {data: project}] = useLazyGetProjectUuidQuery()
            const dateStart= new Date();
        
            const yearStart = dateStart.getFullYear();
            const monthStart = (dateStart.getMonth() + 1).toString().padStart(2, '0');
            const dayStart = dateStart.getDate().toString().padStart(2, '0');
        
            const formattedDateStart = `${yearStart}-${monthStart}-${dayStart}`;
        
            useEffect(() => {
                getProject({uuid: projectUuid})
            },[projectUuid])

    // API хуки
    const [getLaboratoryPlan] = useLazyGetLaboratoryPlanQuery();
    const [postLaboratoryPlan] = usePostLaboratoryPlanMutation();

    // Загрузка данных при открытии модального окна
    useEffect(() => {
        if (isOpen && projectUuid) {
            loadLaboratoryPlanData();
        }
    }, [isOpen, projectUuid]);

    // Функция загрузки данных
    const loadLaboratoryPlanData = async () => {
        if (!projectUuid) return;

        setIsLoading(true);
        try {
            const response = await getLaboratoryPlan({ project_uuid: projectUuid }).unwrap();
            transformLaboratoryPlanData(response);
        } catch (error) {
            console.error('❌ Ошибка при загрузке плана лаборатории:', error);
            // Очищаем данные при ошибке
            setWaterData([]);
            setOilData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Преобразование данных из API согласно примеру ответа
    const transformLaboratoryPlanData = (data: any) => {
        //('📊 Полученные данные лабораторного плана:', data);
        
        const waterRows: LaboratoryPlanRow[] = [];
        const oilRows: LaboratoryPlanRow[] = [];

        // Обрабатываем данные по воде
        if (data?.laboratory_data?.water && Array.isArray(data?.laboratory_data?.water)) {
            data?.laboratory_data?.water.forEach((waterItem: any, index: number) => {
                const row: LaboratoryPlanRow = {
                    id: `water-${index}`,
                    well_number: `Скважина ${waterItem.number}`,
                    water_ic: waterItem.is_icp,
                    water_ha: waterItem.is_hav,
                    water_kapel: waterItem.is_kapel,
                    oil_gh: false,
                    category: 'Исследование воды',
                };
                waterRows.push(row);
            });
        }

        // Обрабатываем данные по нефти
        if (data?.laboratory_data?.oil && Array.isArray(data?.laboratory_data?.oil)) {
            data?.laboratory_data?.oil.forEach((oilItem: any, index: number) => {
                const row: LaboratoryPlanRow = {
                    id: `oil-${index}`,
                    well_number: `Скважина ${oilItem.number}`,
                    water_ic: false,
                    water_ha: false,
                    water_kapel: false,
                    oil_gh: oilItem.is_gh,
                    category: 'Исследование нефти',
                };
                oilRows.push(row);
            });
        }

        setWaterData(waterRows);
        setOilData(oilRows);
    };

    // Функция для отображения иконки проверки
    const renderCheckIcon = (value: boolean) => {
        //@ts-ignore
        return value ? <IconCheck size="s" /> : <Text>-</Text>;
    };

    // Функция для скачивания файла
    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);
    };

    // Обработчик экспорта в Excel
    const handleExportToExcel = async () => {
        if (!projectUuid) {
            console.error('❌ projectUuid не указан');
            return;
        }

        //('📤 Начало экспорта лабораторного плана в Excel для проекта:', projectUuid);
        setIsExporting(true);

        try {
            // Вызываем мутацию и получаем blob
            const blob = await postLaboratoryPlan({ project_uuid: projectUuid }).unwrap();
            
            //('✅ Получен blob для скачивания лабораторного плана');
            
            if (blob instanceof Blob) {
                const filename = `Отчет_по_исследованиям_в_лабораторию_${formattedDateStart}_${project?.name}.xlsx`;
                downloadFile(blob, filename);
                //('✅ Файл Excel с лабораторным планом успешно скачан:', filename);
            } else {
                console.error('❌ Полученные данные не являются Blob:', blob);
            }

        } catch (error: any) {
            console.error('❌ Ошибка при экспорте лабораторного плана в Excel:', error);
            
            // Дополнительная диагностика
            if (error?.data) {
                //('📊 Данные ошибки:', error.data);
                if (error.data instanceof Blob) {
                    //('⚠️ Ошибка содержит blob, пытаемся скачать...');
                    const filename = `Отчет_по_исследованиям_в_лабораторию_${formattedDateStart}_${project?.name}_error.xlsx`;
                    downloadFile(error.data, filename);
                }
            }
        } finally {
            setIsExporting(false);
        }
    };

    // Колонки для таблицы воды
    const columnsWater = [
        {
            title: '№ скважины',
            accessor: 'well_number',
            renderCell: (row: LaboratoryPlanRow) => (
                <Text>{row.well_number}</Text>
            )
        },
        {
            title: 'ICP',
            accessor: 'water_ic',
            renderCell: (row: LaboratoryPlanRow) => renderCheckIcon(row.water_ic)
        },
        {
            title: 'ХАВ',
            accessor: 'water_ha',
            renderCell: (row: LaboratoryPlanRow) => renderCheckIcon(row.water_ha)
        },
        {
            title: 'Капель',
            accessor: 'water_kapel',
            renderCell: (row: LaboratoryPlanRow) => renderCheckIcon(row.water_kapel)
        },
    ];

    // Колонки для таблицы нефти
    const columnsOil = [
        {
            title: '№ скважины',
            accessor: 'well_number',
            renderCell: (row: LaboratoryPlanRow) => (
                <Text>{row.well_number}</Text>
            )
        },
        {
            title: 'ГХ',
            accessor: 'oil_gh',
            renderCell: (row: LaboratoryPlanRow) => renderCheckIcon(row.oil_gh)
        },
    ];

    const handleClose = () => {
        setWaterData([]);
        setOilData([]);
        handleCloseModal();
    };

    return (
        <Modal
            isOpen={isOpen}
            hasOverlay
            onClickOutside={handleClose}
            onEsc={handleClose}
            style={{
                width: '700px',
                maxWidth: '95vw'
            }}
        >
            <div style={{
                padding: '20px', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '20px'
            }}>
                <div style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <Text size='xl' weight='bold'>
                        Лабораторный план исследований
                    </Text>
                    <Button 
                        size='s' 
                        iconLeft={IconClose} 
                        view='clear' 
                        onClick={handleClose}
                    />
                </div>
                
                {isLoading ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        padding: '40px' 
                    }}>
                        <Text>Загрузка лабораторного плана...</Text>
                    </div>
                ) : (
                    <>
                        {/* Таблица исследований воды */}
                        <div>
                            <Text size="l" weight="semibold" style={{ marginBottom: '10px' }}>
                                Исследования воды
                            </Text>
                            {waterData.length > 0 ? (
                                <Table
                                    rows={waterData}
                                    //@ts-ignore
                                    columns={columnsWater} 
                                    stickyHeader
                                    borderBetweenRows 
                                />
                            ) : (
                                <Text view="secondary" style={{ padding: '20px', textAlign: 'center' }}>
                                    Нет данных по исследованиям воды
                                </Text>
                            )}
                        </div>

                        {/* Таблица исследований нефти */}
                        <div>
                            <Text size="l" weight="semibold" style={{ marginBottom: '10px' }}>
                                Исследования нефти
                            </Text>
                            {oilData.length > 0 ? (
                                <Table
                                    rows={oilData}
                                    //@ts-ignore
                                    columns={columnsOil} 
                                    stickyHeader
                                    borderBetweenRows 
                                />
                            ) : (
                                <Text view="secondary" style={{ padding: '20px', textAlign: 'center' }}>
                                    Нет данных по исследованиям нефти
                                </Text>
                            )}
                        </div>

                        {/* Статистика */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '20px',
                            padding: '10px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px'
                        }}>
                            <Text size="s" view="secondary">
                                Скважин с исследованиями воды: <Text weight="bold">{waterData.length}</Text>
                            </Text>
                            <Text size="s" view="secondary">
                                Скважин с исследованиями нефти: <Text weight="bold">{oilData.length}</Text>
                            </Text>
                        </div>
                    </>
                )}

                <div style={{
                    display: 'flex', 
                    justifyContent: 'end', 
                    gap: '20px'
                }}>
                    <Button
                        size="m"
                        view="ghost"
                        label="Закрыть"
                        width="default"
                        onClick={handleClose}
                        disabled={isExporting}
                    />
                    <Button
                        size="m"
                        view="primary"
                        label={isExporting ? "Формирование Excel..." : "Экспорт в Excel"}
                        width="default"
                        onClick={handleExportToExcel}
                        loading={isExporting}
                        disabled={!projectUuid || (waterData.length === 0 && oilData.length === 0) || isExporting}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default MonitoringStage5;